import { createContext, useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import { ElasticSearch } from '@interfaces/models/elasticSearch';
import React, { useMemo, useState } from 'react';
import { removeKeys } from '@helpers/utils/general';
import { CatalogContextProps } from '@interfaces/models/catalogContextProps';
import { getApplyFilterPayload } from '@helpers/utils/catalog/catalog-tracking-utils';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { useBucket } from '@context/bucket.context';

const ORDERED_CATEGORY_MENU_LEVELS = ['universe', 'categoryLvl0', 'categoryLvl1', 'categoryLvl2'] as const;

export type CategoryMenuLevels = (typeof ORDERED_CATEGORY_MENU_LEVELS)[number];

export type ParentMenuLevels = Exclude<CategoryMenuLevels, 'categoryLvl2'>;

const getParentLevel = (level: CategoryMenuLevels): ParentMenuLevels | undefined => {
  const index = ORDERED_CATEGORY_MENU_LEVELS.indexOf(level);
  if (index === 0) {
    return;
  }
  return ORDERED_CATEGORY_MENU_LEVELS[index - 1] as ParentMenuLevels;
};

const getChildLevel = (level: CategoryMenuLevels): CategoryMenuLevels | undefined => {
  const index = ORDERED_CATEGORY_MENU_LEVELS.indexOf(level);
  if (index === ORDERED_CATEGORY_MENU_LEVELS.length - 1) {
    return;
  }
  return ORDERED_CATEGORY_MENU_LEVELS[index + 1];
};

type FacetConfig = {
  id: string;
  name: string;
} | null;

export interface MenuState {
  activeLevel: CategoryMenuLevels;
  firstLevel: CategoryMenuLevels;
  universe: FacetConfig;
  categoryLvl0: FacetConfig;
  categoryLvl1: FacetConfig;
}

interface CatalogCategoryHotfilterContextProps {
  applyCategoryHotFilter: (input: { id: string; facet: CategoryMenuLevels }) => void;
  removeCategoryHotfilter: (input: { id: string; facet: CategoryMenuLevels }) => void;
  removeAllCategoryHotfilters: () => void;
  menuState: MenuState;
  setMenuState: React.Dispatch<React.SetStateAction<MenuState>>;
  parentLevel: ParentMenuLevels | undefined;
  childLevel: CategoryMenuLevels | undefined;
  categoryHotfilterCount: number;
}

const CatalogCategoryHotfilterContext = createContext<CatalogCategoryHotfilterContextProps>({} as never);

export const useCatalogHotfilterMenu = ({
  staticFilterGroups,
  filters,
  facets,
}: Pick<CatalogContextProps, 'staticFilterGroups' | 'filters' | 'facets'>) => {
  const { firstLevel, universe, categoryLvl0, categoryLvl1 } = useMemo<Omit<MenuState, 'activeLevel'>>(() => {
    const getFacet = (facetType: CategoryMenuLevels, filterId: string) => {
      if (!filterId) {
        return null;
      }
      return facets?.[facetType]?.find((x) => String(x.id) === String(filterId));
    };

    const universeFilter = filters['universe.id']?.[0];
    const categoryLvl0Filter = filters['categoryLvl0.id']?.[0];
    const categoryLvl1Filter = filters['categoryLvl1.id']?.[0];

    const universeFacet = getFacet('universe', universeFilter);
    const categoryLvl0Facet = getFacet('categoryLvl0', categoryLvl0Filter);
    const categoryLvl1Facet = getFacet('categoryLvl1', categoryLvl1Filter);

    if (staticFilterGroups.categoryLvl1) {
      return {
        firstLevel: 'categoryLvl2',
        universe: { id: universeFilter, name: universeFacet?.name },
        categoryLvl0: { id: categoryLvl0Filter, name: categoryLvl0Facet?.name },
        categoryLvl1: { id: categoryLvl1Filter, name: categoryLvl1Facet?.name },
      };
    }

    if (staticFilterGroups.categoryLvl0) {
      return {
        firstLevel: 'categoryLvl1',
        universe: { id: universeFilter, name: universeFacet?.name },
        categoryLvl0: { id: categoryLvl0Filter, name: categoryLvl0Facet?.name },
        categoryLvl1: null,
      };
    }

    if (staticFilterGroups.universe) {
      return {
        firstLevel: 'categoryLvl0',
        universe: { id: universeFilter, name: universeFacet?.name },
        categoryLvl0: null,
        categoryLvl1: null,
      };
    }

    return {
      firstLevel: 'universe',
      universe: null,
      categoryLvl0: null,
      categoryLvl1: null,
    };
  }, [filters, staticFilterGroups, facets]);

  return { firstLevel, universe, categoryLvl0, categoryLvl1 };
};

const CatalogCategoryHotfilterProvider: React.FCWithChildren = ({ children }) => {
  const filters = useContextSelector(CatalogContext, (v) => v.filters);
  const setFilters = useContextSelector(CatalogContext, (v) => v.setFilters);
  const facets = useContextSelector(CatalogContext, (v) => v.facets);
  const staticFilterGroups = useContextSelector(CatalogContext, (v) => v.staticFilterGroups);
  const isManualSwitchRef = useContextSelector(CatalogContext, (v) => v.isManualSwitchRef);
  const { firstLevel, universe, categoryLvl0, categoryLvl1 } = useCatalogHotfilterMenu({
    staticFilterGroups,
    facets,
    filters,
  });
  const { isFeatureEnabled } = useBucket();
  const isSelectMultipleCategoriesEnabled = isFeatureEnabled((f) => f.enableMultipleCategoryFilterSelectionOnPlp);

  const { sendAnalyticEvent } = useAnalyticEvents('catalog');

  const [menuState, setMenuState] = useState<MenuState>({
    activeLevel: firstLevel,
    firstLevel,
    universe,
    categoryLvl0,
    categoryLvl1,
  });

  const { parentLevel, childLevel } = useMemo(() => {
    return {
      parentLevel: getParentLevel(menuState.activeLevel),
      childLevel: getChildLevel(menuState.activeLevel),
    };
  }, [menuState.activeLevel]);

  const applyCategoryHotFilter = ({ id, facet }: { id: string; facet: CategoryMenuLevels }) => {
    isManualSwitchRef.current = true;
    const sendSnowplowEvent = (filter_details: ElasticSearch['filters'], filters_applied: { [key: string]: string }) =>
      sendAnalyticEvent('apply_filter', getApplyFilterPayload(filter_details, filters_applied));

    if (facet === 'universe') {
      const newState: ElasticSearch['filters'] = removeKeys(
        {
          ...filters,
          'universe.id': [menuState.universe.id],
        },
        ['categoryLvl2.id', 'categoryLvl1.id', 'categoryLvl0.id'],
      );

      sendSnowplowEvent(newState, { 'universe.id': id });
      setFilters(newState);
      return;
    }

    if (facet === 'categoryLvl0') {
      if (!menuState?.universe?.id) {
        return;
      }

      // there is an override to allow merch team to select multiple catLvl0 filters to build campaigns
      if (isSelectMultipleCategoriesEnabled) {
        const newState: ElasticSearch['filters'] = {
          ...filters,
          'universe.id': [menuState.universe.id],
          'categoryLvl0.id': [...(filters['categoryLvl0.id'] ?? []), id],
        };
        setFilters(newState);
        return;
      }
      // normally we only allow one categoryLvl0 to be selected at a time
      const newState: ElasticSearch['filters'] = removeKeys(
        {
          ...filters,
          'universe.id': [menuState.universe.id],
          'categoryLvl0.id': [id],
        },
        ['categoryLvl2.id', 'categoryLvl1.id'],
      );

      sendSnowplowEvent(newState, { 'categoryLvl0.id': id });
      setFilters(newState);
      return;
    }

    if (facet === 'categoryLvl1') {
      if (!menuState?.categoryLvl0?.id || !menuState?.universe?.id) {
        return;
      }

      // we allow multiple categoryLvl1 to be selected at a time, but they need to have the same parent
      const currentCatLvl1Filters = filters['categoryLvl1.id'] || [];
      const currentCatLvl0Filters = filters['categoryLvl0.id'] || [];

      // there is an override to allow merch team to select multiple catLvl` filters to build campaigns
      if (isSelectMultipleCategoriesEnabled) {
        const newState: ElasticSearch['filters'] = {
          ...filters,
          'universe.id': [menuState.universe.id],
          'categoryLvl0.id': [...new Set([...currentCatLvl0Filters, menuState.categoryLvl0.id])],
          'categoryLvl1.id': [...new Set([...currentCatLvl1Filters, id])],
        };
        setFilters(newState);
        return;
      }

      // filter all catLvl1Filters that have parentId equal to the current categoryLvl0
      const applicableCatLvl1Filters = facets?.categoryLvl1
        .filter((catLvl1) => String(catLvl1.parentID) === menuState.categoryLvl0.id)
        .map((catLvl1) => catLvl1.id);
      // filter out only the filter ids that are in applicableCatLvl1Filters
      const remainingCatLvl1Filters = currentCatLvl1Filters.filter((filter) =>
        applicableCatLvl1Filters.includes(filter),
      );

      const newCatLvl1Filters = new Set([...remainingCatLvl1Filters, id]);

      const newState: ElasticSearch['filters'] = removeKeys(
        {
          ...filters,
          'universe.id': [menuState.universe.id],
          'categoryLvl0.id': [menuState.categoryLvl0.id],
          'categoryLvl1.id': [...newCatLvl1Filters],
        },
        ['categoryLvl2.id'],
      );

      sendSnowplowEvent(newState, { 'categoryLvl1.id': id });
      setFilters(newState);
      return;
    }

    if (facet === 'categoryLvl2') {
      if (!menuState?.categoryLvl1?.id || !menuState?.categoryLvl0?.id || !menuState?.universe?.id) {
        return;
      }
      // we allow multiple categoryLvl2 to be selected at a time, but they need to have the same parent
      const currentCatLvl2Filters = filters['categoryLvl2.id'] || [];
      // filter all catLvl2Filters that have parentId equal to the current categoryLvl1
      const applicableCatLvl2Filters = facets?.categoryLvl2
        .filter((catLvl2) => String(catLvl2.parentID) === menuState.categoryLvl1.id)
        .map((catLvl2) => catLvl2.id);
      // filter out only the filter ids that are in applicableCatLvl2Filters
      const remainingCatLvl2Filters = currentCatLvl2Filters.filter((filter) =>
        applicableCatLvl2Filters.includes(filter),
      );
      const newCatLvl2Filters = new Set([...remainingCatLvl2Filters, id]);
      const newState: ElasticSearch['filters'] = {
        ...filters,
        'universe.id': [menuState.universe.id],
        'categoryLvl0.id': [menuState.categoryLvl0.id],
        'categoryLvl1.id': [menuState.categoryLvl1.id],
        'categoryLvl2.id': [...newCatLvl2Filters],
      };

      sendSnowplowEvent(newState, { 'categoryLvl2.id': id });
      setFilters(newState);
    }
    return;
  };

  const removeAllCategoryHotfilters = () => {
    if (staticFilterGroups.categoryLvl2) {
      return;
    }
    isManualSwitchRef.current = true;
    if (staticFilterGroups.categoryLvl1) {
      const newState = removeKeys(filters, ['categoryLvl2.id']);
      setFilters(newState);
      return;
    }
    if (staticFilterGroups.categoryLvl0) {
      const newState = removeKeys(filters, ['categoryLvl2.id', 'categoryLvl1.id']);
      setFilters(newState);
      return;
    }
    if (staticFilterGroups.universe) {
      const newState = removeKeys(filters, ['categoryLvl2.id', 'categoryLvl1.id', 'categoryLvl0.id']);
      setFilters(newState);
      return;
    }
    const newState = removeKeys(filters, ['categoryLvl2.id', 'categoryLvl1.id', 'categoryLvl0.id', 'universe.id']);
    setFilters(newState);
  };

  const removeCategoryHotfilter = ({ id, facet }: { id: string; facet: CategoryMenuLevels }) => {
    isManualSwitchRef.current = true;
    if (facet === 'universe') {
      // if user unselects a universe filter, we clear all category filters
      removeAllCategoryHotfilters();
      return;
    }
    if (facet === 'categoryLvl0') {
      // if user unselected a catLvl0 filter, we clear all category filters
      removeAllCategoryHotfilters();
      return;
    }
    if (facet === 'categoryLvl1') {
      // if there are other catLvl1 filters, just remove the selected one
      if (filters['categoryLvl1.id']?.length > 1) {
        const newState = {
          ...filters,
          'categoryLvl1.id': filters['categoryLvl1.id'].filter((filter) => filter !== id),
        };
        setFilters(newState);
        return;
      }
      // if there are no other catLvl1 filters, clear all category filters
      removeAllCategoryHotfilters();
    }
    if (facet === 'categoryLvl2') {
      // if there are other catLvl2 filters, just remove the selected one
      if (filters['categoryLvl2.id']?.length > 1) {
        const newState = {
          ...filters,
          'categoryLvl2.id': filters['categoryLvl2.id'].filter((filter) => filter !== id),
        };
        setFilters(newState);
        return;
      }
      // if there are no other catLvl2 filters, clear all filters
      removeAllCategoryHotfilters();
    }
  };

  const categoryHotfilterCount = useMemo(() => {
    const catLvl2Count = filters['categoryLvl2.id']?.length || 0;
    const catLvl1Count = filters['categoryLvl1.id']?.length || 0;
    const catLvl0Count = filters['categoryLvl0.id']?.length || 0;
    const universeCount = filters['universe.id']?.length || 0;
    return catLvl2Count || catLvl1Count || catLvl0Count || universeCount;
  }, [filters]);

  return (
    <CatalogCategoryHotfilterContext.Provider
      value={{
        applyCategoryHotFilter,
        removeCategoryHotfilter,
        removeAllCategoryHotfilters,
        menuState,
        setMenuState,
        parentLevel,
        childLevel,
        categoryHotfilterCount,
      }}
    >
      {children}
    </CatalogCategoryHotfilterContext.Provider>
  );
};

export { CatalogCategoryHotfilterProvider, CatalogCategoryHotfilterContext };
